import { IxSigner } from "@shared/src/stores/useAuthStore";
import { createMessage, getMessageBody, getNonce, verify } from "@shared/src/lib/auth/siwe";
import { AuthParams } from "@shared/src/hooks/auth/types";
import { OrderResponse, OrderStateKey } from "@sdk/shared/types/order.types";
import { getErrorMessage, getTradeRoles } from "@shared/src/utils";
import { SiweMessage } from "siwe";
import chains from "@shared/src/config/chains";
import { createPublicClient, Hex, http, WalletClient } from "viem";
import { useHostStore } from "@shared/src/stores/useHostStore";
import { AlchemySigner } from "@account-kit/core";

export async function siweVerify(payload: { message: string; signature: string }) {
  let siweMessage = new SiweMessage(payload.message);
  console.log("siwe verify", { payload, siweMessage });

  console.log("siwe verify failed, trying smart contract verification");
  // Smart contract verification
  const chain = chains.find(c => c.id === siweMessage.chainId);
  if (!chain) {
    throw new Error(`Unsupported chain ID: ${siweMessage.chainId}`);
  }

  // Create a public client for the specific chain
  const publicClient = createPublicClient({
    chain: chain,
    transport: http(chain.rpcUrls.default.http[0]),
  });

  // Verify the message
  const isValid = await publicClient.verifyMessage({
    address: siweMessage.address as `0x${string}`,
    message: siweMessage.prepareMessage(),
    signature: payload.signature as `0x${string}`,
  });
  console.log("siwe verify", { isValid });

  if (isValid) {
    return isValid;
  } else {
    throw new Error("Invalid Siwe...");
  }
}

export async function getJwtWithSiwe(signer: IxSigner, address: Hex, chainId: number) {
  await useHostStore.getState().setAndSwitchChain(chainId); // Base Mainnet
  const nonce = await getNonce();
  const siweMessage = createMessage(nonce, address, chainId);
  const message = getMessageBody(siweMessage);
  const messageHex = `0x${Buffer.from(message, "utf8").toString("hex")}` as const;

  try {
    const signature = await signer.signMessage(message);

    console.log("siwe verify", { message, signature, messageHex });
    await siweVerify({ message, signature });
    const jwt = await verify(message, signature);

    return jwt;
  } catch (e) {
    console.warn(e);
    if (e instanceof Error) {
      if (e.stack?.includes("signMessage")) {
        throw new Error("Error signing SIWE message");
      } else if (e.stack?.includes("verify")) {
        throw new Error("Error verifying SIWE message");
      } else {
        throw new Error("Unknown error during SIWE message processing");
      }
    } else {
      const errorMessage = getErrorMessage(e as any);
      throw new Error("Unknown siwe error", { cause: errorMessage });
    }
  }
}

export function getStatusChangedMessage(newState: OrderStateKey, orderData: OrderResponse) {
  const { isMerchant, isBuyer, isSeller } = getTradeRoles(orderData.merchant.address, orderData.type);

  switch (newState) {
    case "accepted":
      if (isMerchant) {
        return;
      }
      return `Merchant has Accepted the Order`;
    case "paid":
      if (isSeller) {
        return "Buyer Marked Paid";
      }
      return;
    case "released":
      if (isBuyer) {
        return "Seller Released Funds";
      }
      return;
    case "cancelled":
      return `Order has been cancelled`;
    case "appealed":
      return `Order has been appealed`;
  }
}

export function extractAuthParams(params: AuthParams) {
  const { type, onSuccess } = params;
  const email = "email" in params ? params.email : undefined;
  const otpCode = "otpCode" in params ? params.otpCode : undefined;
  const emailMode = "emailMode" in params ? params.emailMode : undefined;
  const authProviderId = "authProviderId" in params ? params.authProviderId : undefined;
  const mode = "mode" in params ? params.mode : undefined;
  const connector = "connector" in params ? params.connector : undefined;
  const reownJwt = "reownJwt" in params ? params.reownJwt : undefined;
  const connectorIcon = "connectorIcon" in params ? params.connectorIcon : undefined;

  return {
    type,
    onSuccess,
    email,
    otpCode,
    emailMode,
    authProviderId,
    mode,
    connector,
    reownJwt,
    connectorIcon,
  };
}
